<script lang="ts">
	import { page } from '$app/stores';
	import { createEventDispatcher } from 'svelte';
	import { getTranslate } from '@tolgee/svelte';
	import Button from '../Button/Button.svelte';
	import { logInWithSocial } from '$lib/service/supabase/user.service';
	import { classes } from '$lib/utils';
	import LinkedInSignInIcon from '../Icon/LinkedInSignInIcon.svelte';
	import GoogleSignInIcon from '../Icon/GoogleSignInIcon.svelte';
	import type { Provider } from '@supabase/supabase-js';
	import MailSignInIcon from '../Icon/MailSignInIcon.svelte';
	import { SIGN_UP_STEP, SIGN_UP_URL } from '$lib/constants';
	import { browser } from '$app/environment';
	import { goto } from '$app/navigation';
	import { profileApi } from '$lib/service/supabase/profile.api';

	const { t } = getTranslate();
	const dispatch = createEventDispatcher();

	export let additionalClass = '';
	export let additionalButtonClass = '';

	let loading = false;
	let serverError: string = '';

	$: $page.data.session?.user.id && redirectIfAlreadyRegistered($page.data.session?.user?.id!);

	const handleSignUp = async (provider: Provider) => {
		loading = true;

		try {
			await logInWithSocial(
				provider,
				{
					redirectTo: `${window.location.origin}/auth/callback`
				},
				true
			);

			// Socials don't return user - so just dispatch
			// success here without further detail
			dispatch('success');
		} catch (error: any) {
			console.error('ERROR while logging in', error);
			const { message } = error;
			serverError = message;
			dispatch('error', error);
		} finally {
			loading = false;
		}
	};

	const handleEmailSignUp = () => {
		dispatch('emailSuccess');
	};

	async function redirectIfAlreadyRegistered(id: string) {
		// Guard if metadata are still missing - don't redirect away from the signup.
		if (localStorage.getItem(SIGN_UP_STEP)) return;

		const profile = await profileApi.getById(id);
		// Guard if no profile exists - don't redirect away from the signup.
		if (!profile) return;

		if (profile?.username && browser) {
			let url: URL | string = '/';

			if (localStorage.getItem(SIGN_UP_URL)) {
				url = new URL(localStorage.getItem(SIGN_UP_URL) as string);
			}

			goto(url);
		}
	}
</script>

<div class={classes(additionalClass, 'flex flex-col items-center justify-center')}>
	<div class="space-y-4 flex flex-col justify-center w-[400px] whitespace-nowrap items-center">
		<Button
			on:click={() => handleSignUp('google')}
			additionalClass={classes(
				additionalButtonClass,
				'mt-4 w-full !text-purple bg-white !border-purple hover:bg-purple hover:!text-white'
			)}
			><div class="flex flex-row items-center stroke-2 gap-2">
				<GoogleSignInIcon additionalClass="text-white" />{$t('signUp.with_google.button')}
			</div></Button
		>
		<Button
			on:click={() => handleSignUp('linkedin_oidc')}
			additionalClass={classes(
				additionalButtonClass,
				'mt-4 w-full !text-purple bg-white !border-purple hover:bg-purple hover:!text-white'
			)}
			><div class="flex flex-row items-center stroke-2 gap-2">
				<LinkedInSignInIcon additionalClass="text-white -mt-1" />{$t('signUp.with_linkedin.button')}
			</div></Button
		>
		<Button
			on:click={handleEmailSignUp}
			additionalClass={classes(
				additionalButtonClass,
				'mt-4 w-full !text-purple bg-white !border-purple hover:bg-purple hover:!text-white'
			)}
			><div class="flex flex-row items-center stroke-1 gap-2">
				<MailSignInIcon additionalClass="text-white -mt-1" />{$t('signUp.with_email.button')}
			</div></Button
		>
		{#if serverError}
			<div class="text-error">{serverError}</div>
		{/if}
	</div>
</div>
